import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import moment from 'moment';

import { FBoxContainer } from '../../../components/base';
import ActionRequiredCard from './ActionRequiredCard';
import CardLoader from './CardLoader';
import { ActionRequiredItemEnum, BTSessionStatus } from '../../../constants';
import { FORTA_BLUE, FORTA_SUCCESS } from '../../../utils/colors';

import type { RootState } from '../../../store';
import type { IActionRequiredItem } from '../../../utils/types';

function SignatureNeededSection() {
  const { list: btSupervisions = [], loading: btSupervisionLoading } = useSelector(
    (state: RootState) => state.btSupervision,
  );
  const { list: parentSupervisions = [], loading: parentSupervisionLoading } = useSelector(
    (state: RootState) => state.parentSupervision,
  );
  const { list: clientSupervisions = [], loading: clientSupervisionsLoading } = useSelector(
    (state: RootState) => state.clientSupervisions,
  );
  const { list: groupSupervisions = [], loading: groupSupervisionsLoading } = useSelector(
    (state: RootState) => state.groupParentSupervision,
  );
  const { list: clientAssessments = [], loading: clientAssessmentLoading } = useSelector(
    (state: RootState) => state.clientAssessment,
  );
  const { list: treatmentPlans, loading: treatmentPlansLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );
  const { list: sessions, loading: sessionsLoading } = useSelector(
    (state: RootState) => state.sessions,
  );

  const loading =
    btSupervisionLoading ||
    parentSupervisionLoading ||
    clientSupervisionsLoading ||
    groupSupervisionsLoading ||
    clientAssessmentLoading ||
    treatmentPlansLoading ||
    sessionsLoading;

  const filteredSessions = useMemo(() => {
    return sessions.filter(
      (session) =>
        session.status === BTSessionStatus.EditsRequested ||
        session.status === BTSessionStatus.Started,
    );
  }, [sessions]);

  const actionRequired = useMemo(() => {
    const mapToActionRequiredItem = (
      items: any[],
      type: ActionRequiredItemEnum,
    ): IActionRequiredItem[] =>
      items.map((item) => ({
        id: item?.id ?? 0,
        type: type,
        item,
        clientName: '',
        date_start:
          type === ActionRequiredItemEnum.TreatmentPlan
            ? item.treatment_recommendation?.date_start
            : item.date_start,
        status: (item.status?.label ? item.status?.label : item.status) ?? '',
        duration: item.duration ?? item.expected_duration,
      }));

    return [
      ...mapToActionRequiredItem(btSupervisions, ActionRequiredItemEnum.Bt),
      ...mapToActionRequiredItem(clientSupervisions, ActionRequiredItemEnum.Client),
      ...mapToActionRequiredItem(groupSupervisions, ActionRequiredItemEnum.GroupParent),
      ...mapToActionRequiredItem(parentSupervisions, ActionRequiredItemEnum.Parent),
      ...mapToActionRequiredItem(treatmentPlans, ActionRequiredItemEnum.TreatmentPlan),
      ...mapToActionRequiredItem(clientAssessments, ActionRequiredItemEnum.Assessment),
      ...mapToActionRequiredItem(filteredSessions, ActionRequiredItemEnum.Session),
    ].sort((a, b) => moment(a.date_start).diff(moment(b.date_start))); // Sort by age of the item, oldest first
  }, [
    btSupervisions,
    clientSupervisions,
    groupSupervisions,
    parentSupervisions,
    treatmentPlans,
    clientAssessments,
    filteredSessions,
  ]);

  return (
    <FBoxContainer
      sx={{ flexGrow: 0, textAlign: 'left', height: 450 }}
      contentStyle={{ pr: actionRequired?.length > 3 ? 1 : 2 }}
    >
      <Typography sx={{ color: FORTA_BLUE.primary, fontSize: 18, fontWeight: 500 }}>
        Signature Needed
      </Typography>
      <Box sx={{ overflowY: 'auto', height: 360, mt: 2.5 }}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <CardLoader sx={{ mr: 1 }} key={i} />)
        ) : actionRequired?.length === 0 ? (
          <Box
            sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <CheckCircleIcon sx={{ color: FORTA_SUCCESS }} />
            <Typography ml={0.5}>You&apos;re all caught up!</Typography>
          </Box>
        ) : (
          actionRequired.map(({ id, item, type, date_start, status, duration }) => (
            <ActionRequiredCard
              key={`${type}-${id}`}
              item={item}
              dateStart={date_start}
              type={type}
              id={id}
              duration={duration}
              status={status ?? ''}
            />
          ))
        )}
      </Box>
    </FBoxContainer>
  );
}

export default SignatureNeededSection;
